import React, { ReactNode } from "react";
import Color from "color";
import { toDataTest } from "helpers/test";
import XssSanitizedContent from "components/helpers/xss-sanitized-content";
import { amphibiousToJS } from "helpers/objUtils";
import { themeOptions } from "styles/theme";

interface IProps {
    label: string;
    value: any;
    type?: AttributeType;
    valueLabel?: string;
    children?: ReactNode;
    printType?: string;
}

export enum AttributeType {
    Text,
    Color,
    Swatch,
    Image,
    Complex,
}

export const isContrastRequired = (hexColor = "white") => Color(hexColor).contrast(Color("white")) < 2;
export const isValidHexColor = (value: string): boolean => /^#([0-9a-fA-F]{3}){1,2}$/.test(value);

export const formatColorValue = (value: string, valueLabel: string = "") => {
    const valid = isValidHexColor(value);
    const style = {
        backgroundColor: valid ? value : undefined,
        border: valid && isContrastRequired(value) ? `1px solid ${themeOptions.palette.grey}` : "none",
    };
    const displayColor = valid ? valueLabel : value;

    return (
        <>
            {valid && <div className="color" style={style} />} {displayColor}
        </>
    );
};

export const Attribute: React.FC<IProps> = ({
    label,
    value = "",
    valueLabel = "",
    type = AttributeType.Text,
    children,
    printType,
}) => {
    const dataTest = `attribute-${toDataTest(label)}`;
    switch (type) {
        case AttributeType.Color: {
            return (
                <div className="attribute" data-test={dataTest}>
                    <div className="label">{label}</div>
                    <div className="value">{formatColorValue(value, valueLabel)}</div>
                </div>
            );
        }
        case AttributeType.Image: {
            return (
                <div className="attribute" data-test={dataTest}>
                    <div className="label">{label}</div>
                    <div className="value">
                        <div className="image">
                            {printType && <div className="printType">{printType}</div>}
                            <img src={value} />
                            {children}
                        </div>
                    </div>
                </div>
            );
        }
        case AttributeType.Complex: {
            return (
                <div className="attribute" data-test={dataTest}>
                    <div className="label">{label}</div>
                    <div className="value complex">
                        <div className="main">{value}</div>
                        {children}
                    </div>
                </div>
            );
        }
        default:
            if (React.isValidElement(value)) {
                return (
                    <div className="attribute" data-test={dataTest}>
                        <div className="label">{label}</div>
                        <div className="value">{value}</div>
                    </div>
                );
            } else {
                return (
                    <div className="attribute" data-test={dataTest}>
                        <div className="label">{label}</div>
                        <div className="value">
                            <XssSanitizedContent
                                content={value.toJS ? amphibiousToJS(value).join(" ") : value.toString()}
                            />
                        </div>
                    </div>
                );
            }
    }
};
